<script lang="ts" setup>
	import { useSeoSchema } from '~/utils/seo-schema'
	import { ProductCode } from '~/types'
	import type { H1Config } from '~/components/ui/h1.vue'
	import type { FAQContext } from '~/components/main/faq.vue'
	import type { teamBenefitsContext } from '~/components/main/productivity-benefits.vue'
	import type { GuideListContext } from '~/components/molecules/important-guides.vue'
	import type { ProcessesStepContext } from '~/components/molecules/process-steps.vue'

	definePageMeta({
		layout: 'default'
	})
	const seo = {
		title: 'MyCoverGenius | Travel cover | Travel insurance for businesses',
		description:
			'Travel insurance covers your business against the risks and losses associated with traveling outside Nigeria. ',
		tags: ['Travel insurance', 'Travel cover', 'Travel insurance for businesses'],
		url: `${import.meta.env.VITE_BUSINESS_URL}/travel-cover`
	}
	useSeoMeta({
		title: seo.title,
		description: seo.description,
		ogTitle: seo.title,
		ogDescription: seo.description,
		ogType: 'website',
		ogLocale: 'en_NG',
		articleTag: seo.tags,
		ogUrl: seo.url,
		twitterTitle: seo.title,
		twitterDescription: seo.description,
		twitterSite: '@mycovergenius',
		twitterCard: 'summary_large_image',
		twitterAppUrlGoogleplay: seo.url,
		twitterAppUrlIphone: seo.url,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
	const isDialogOpen = shallowRef(false)
	const { navigateToProduct, viewMoreProducts } = useProductsStore()
	const benefitHighlights = [
		{
			title: 'Travel Insurance Certificate',
			description:
				'Get the insurance certificate instantly via email, or from your dashboard when you process the insurance',
			image: '/v1727082971/instant-cert_dzjami.webp'
		},
		{
			title: 'User-Friendly Dashboard',
			description: 'Effortlessly oversee and manage staff trips from a single, user-friendly dashboard.',
			image: '/v1727082972/highlight-2_ua3v53.webp'
		},
		{
			title: 'Easy Onboarding',
			description: 'Onboard your staff through our simplified batch upload process.',
			image: '/v1727082969/seamless-onboarding_ridiu1.webp'
		},
		{
			title: 'Affordability',
			description: 'Enjoy affordable travel insurance without compromising on coverage.',
			image: '/v1727396523/budget-friendly_dps9iw.webp'
		},
		{
			title: '100% Digital claims',
			description: 'Streamlined claims process with our fully digital experience—no more paperwork stress.',
			image: '/v1727082983/scp-travel_z0zdax.webp'
		},
		{
			title: '24/7 Customer support',
			description:
				"Rest assured, we're here for you whenever you need help, with your dedicated account manager.",
			image: '/v1727082977/round-support_tnzdsy.webp'
		},
		{
			title: 'Employee Mobile App',
			description:
				'From delayed trip to cancelled flights or baggage loss, MyCover App makes it easy for claims to happen',
			image: '/v1761842696/mobile-comms_wsec6w.webp'
		}
	]
	const faqList = ref<FAQContext[]>([
		{
			question: 'What is Travel Cover',
			answer: 'MCG’s Travel Cover provides comprehensive protection for you and your team when traveling internationally',
			open: true
		},
		{
			question: 'What medical expenses are covered under the Travel Cover plan?',
			answer: 'The plan covers medical evacuation, repatriation, transport to medical centers, and medical expenses up to EUR30,000 for Schengen and EUR35,000 for worldwide coverage.',
			open: false
		},
		{
			question: 'What is the excess for medical-related claims?',
			answer: 'The excess is EUR50 for Schengen coverage and EUR50 for worldwide coverage.',
			open: false
		}
	])

	const { generateProductSchema, generateFaqSchema, generateBreadcrumbSchema } = useSeoSchema()

	useHead({
		script: [
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateProductSchema({
						name: 'Travel Cover',
						description: seo.description,
						image: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082983/travel-hero_rjlb9a.webp',
						url: seo.url,
						price: '0'
					})
				)
			},
			{ type: 'application/ld+json', innerHTML: JSON.stringify(generateFaqSchema(faqList.value)) },
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateBreadcrumbSchema([
						{ name: 'Home', item: 'https://mycovergenius.com/' },
						{ name: 'Travel Cover', item: seo.url }
					])
				)
			}
		],
		link: [{ rel: 'canonical', href: seo.url }]
	})

	const onboardingProcesses = shallowRef<ProcessesStepContext>([
		{
			icon: {
				dir: 'random',
				icon: 'travel-wishlist',
				height: '80',
				width: '80'
			},
			title: 'Provide trip details',
			description:
				'Enter the necessary details of your upcoming trip, including trip details manually or through our batch upload process.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'travel-card',
				height: '100',
				width: '100'
			},
			title: 'Make payment & onboard staff',
			description:
				'Proceed to make the payment for the travel insurance cover and onboard your staff to the plan.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'travel-briefcase',
				height: '80',
				width: '80'
			},
			title: 'Get the certificate',
			description:
				'After completing the payment and onboarding process, receive your travel insurance certificate fast via email.'
		}
	])

	const btnClass: H1Config = {
		tag: { base: 'bg-main text-white sm:hidden capitalize' },
		wrapperClass: {
			alignItems: '',
			overflow: '',
			maxWidth: 'max-w-[587px]'
		},
		hClass: {
			wrapper: {
				alignItems: '',
				size: 'text-[2.75rem]/[3.135rem] -tracking-[0.0825rem] md:text-[3.625rem]/[3.83rem] md:-tracking-[0.10rem] lg:text-[4.25rem]/[4.5rem] lg:-tracking-[0.135rem]'
			}
		},
		paragraphClass: {
			maxWidth: 'max-w-[586px]',
			text: 'xl:text-xl text-base md:text-lg',
			base: 'md:leading-[32px] sm:mb-10 lg:mb-20 mt-3 xl:mt-5'
		},
		btn: {
			base: ' mb-10 lg:mb-0',
			primary: {
				btn: {
					base: 'bg-white  text-base text-travel-600 hover:text-white',
					onHoverPseudo: 'hover:before:bg-travel-500'
				},
				text: {
					size: 'text-sm md:text-base'
				}
			},
			secondary: {
				base: 'default-btn  max-w-[405px] text-center justify-center w-full gap-x-3',
				text: 'text-sm md:text-base'
			}
		}
	}
	const guideList: GuideListContext = [
		{
			tab: 'claim_process',
			label: 'Simple claims process',
			description: "We get the urgency - Simply report a claim from your dashboard and we'll get right on it.",
			list: [
				{
					iconTag: {
						dir: 'random',
						icon: 'clipboard-text',
						height: 16
					},
					title: 'Report Claim',
					description: 'Access your dashboard from any device to report a claim quickly and conveniently.',
					imageSrc: '/v1727082827/travel-claim_buxsaq.gif',
					show: true
				},
				{
					title: 'Claims assessment',
					description: 'We promptly assess and review your claims to ensure a smooth process.',
					iconTag: {
						dir: 'random',
						icon: 'clock'
					},
					imageSrc: '/v1727082822/travel-claim-2_vwuu84.gif'
				},
				{
					title: 'Fast payout',
					description:
						'Enjoy the sweet benefits of your travel insurance with quick payouts for approved claims.',
					iconTag: {
						dir: 'random',
						icon: 'receipt'
					},
					imageSrc: '/v1727082957/gadget-claim-3_rals23.webp'
				}
			]
		}
	]
	const teamBenefits: teamBenefitsContext = [
		{
			title: 'Death or permanent disability',
			description:
				'Ensure financial protection for your employees in the event of accidental death or permanent disability while traveling for business.',
			icon: {
				icon: 'wheel-chair',
				dir: 'random'
			}
		},
		{
			title: 'Cancellation & curtailment',
			description:
				'Safeguard your business from unexpected expenses due to trip cancellations or early returns with our travel insurance coverage.',
			icon: {
				icon: 'cancel',
				dir: 'random'
			}
		},
		{
			title: 'Loss or theft of baggage',
			description:
				'Protect valuable business belongings and trade samples from loss or theft during travel with our travel insurance plan.',
			icon: {
				icon: 'travel-thief',
				dir: 'random'
			}
		},
		{
			title: 'Baggage delay',
			description: 'Mitigate the inconvenience caused by delayed baggage during business trips.',
			icon: {
				icon: 'luggages',
				dir: 'random'
			}
		},
		{
			title: 'Body injury & material damage',
			description:
				'Cover medical expenses and property damage resulting from bodily injury or accidents while traveling for business.',
			icon: {
				icon: 'injury',
				dir: 'random'
			}
		}
	]
	const showBenefits = shallowRef(false)
</script>

<template>
	<div>
		<header class="bg-travel-600 px-5 flexed flex-col justify-center md:px-0 sm:pb-24 py-10 sm:pt-24">
			<ui-pad
				:ui="{ paddingBlock: '', paddingInline: 'sm:px-10', width: 'max-w-[1400px]' }"
				class="sm:inline-flex items-center justify-between w-full lg:flex-nowrap flex-wrap gap-y-10"
			>
				<ui-h1
					tag="travel cover"
					:tag-icon="{ dir: 'random', icon: 'airplane-2', pathClass: 'fill-white' }"
					paragraph="Whether it is for events, seminars or regular trips, let your team travel with confidence, knowing they’re covered."
					:ui="btnClass"
					primary-btn-text="Get your trips covered in 5 minutes <span class='max-sm:hidden'>or less</span> "
					secondary-btn-text="Speak to us to get on board"
					:action="{ type: 'onboard', product: ProductCode.TRAVEL_ALLIANZ }"
				>
					<template #title="{ highlightColor }">
						<span class="block text-wrap">
							Those who will travel happily must
							<span :class="[highlightColor]"> travel insured.</span>
						</span>
					</template>
				</ui-h1>

				<animated-travel
					alt="Traveler with suitcase representing international travel insurance"
					src="/v1727082983/travel-hero_rjlb9a.webp"
					class="flex-none"
					:ui="{ imgWrapper: { base: 'max-md:w-full max-md:ml-auto max-w-[310px] md:max-w-[570px]' } }"
				/>
			</ui-pad>
		</header>

		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-partners />
			<ui-spacer />
			<main-benefit-description
				:highlighted-benefits="benefitHighlights"
				title="Comprehensive solutions for a seamless travel experience"
				:ui="{ li: { activeClass: 'border-travel-500', activeText: 'text-travel-500' } }"
			>
				<div class="max-w-[712px] w-full text-center mx-auto flexed flex-col">
					<p class="sm:text-xl text-base text-gray-600 mt-5">
						We've created features to make your business onboarding experience and plans management
						seamless.
					</p>
				</div>
			</main-benefit-description>
			<ui-spacer />
			<main-productivity-benefits
				title="Enjoy comprehensive coverage across the globe"
				description="With our global travel coverage, your team will always be protected on every trip"
				:team-benefits="teamBenefits"
				product-name="travel"
				btn-label="Discover more about travel cover"
				:ui="{
					container: {
						btn: {
							base: 'bg-travel-500 min-h-[56px]',
							onHoverPseudo: 'hover:before:bg-travel-600'
						}
					}
				}"
				@trigger="showBenefits = true"
			>
				<template #hover-icon>
					<tools-icon dir="random" icon="airplane-2" path-class="fill-white" :height="15" />
				</template>
			</main-productivity-benefits>
			<ui-spacer />
			<molecules-information-hint category="travel" />
			<ui-spacer />
			<molecules-process-steps
				title="Affordable. Digital. Fast."
				description="Cover your team's trips in 3 easy steps!"
				:processes="onboardingProcesses"
				:ui="{ strokeText: { strokeColor: '#B7AAEE', color: 'text-travel-100' }, header: { width: '' } }"
				@trigger="() => (isDialogOpen = true)"
			>
				<template #btn>
					<ui-btn
						:ui="{
							btn: {
								base: 'bg-travel-500 mt-8 min-h-[56px] hover:bg-travel-600',
								onHoverPseudo: 'hover:before:bg-travel-600'
							},
							text: {
								base: 'text-white'
							}
						}"
						@click="navigateToProduct(ProductCode.TRAVEL_ALLIANZ)"
					>
						<span>Get Covered Now</span>
						<template #icon>
							<tools-icon dir="random" icon="airplane-2" path-class="fill-white" :height="15" />
						</template>
					</ui-btn>
				</template>
			</molecules-process-steps>
		</ui-pad>
		<ui-spacer />

		<molecules-important-guides :guide-list="guideList" />
		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-testimonials />
		</ui-pad>
		<ui-spacer />
		<molecules-demo-card
			description=""
			title="Comprehensive coverage for your business."
			type="travel"
			@secondary-click="viewMoreProducts('travel')"
		/>
		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-investors class="pt-12 pb-20" />
			<main-faq :faq-list="faqList" />
		</ui-pad>
		<ui-spacer />

		<molecules-benefit-dialog
			v-model:show="showBenefits"
			:product-name="ProductCode.TRAVEL_ALLIANZ"
			product-category="travel"
		/>
		<molecules-auth-dialog v-model="isDialogOpen" selected="register" />
	</div>
</template>
